import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  clampFloatingMenuPosition,
  positionMenuBesideAnchor,
  snapshotRect,
} from '../utils/floatingMenuPosition';

const liveHandleRect = (blockId) => {
  if (!blockId) return null;
  const handle = document.querySelector(
    `[data-block-id="${blockId}"] > .block-drag-handle`
  );
  return handle ? snapshotRect(handle.getBoundingClientRect()) : null;
};

/**
 * Portaled fixed menu for drag-handle (or cursor) context actions.
 *
 * - `anchorBlockId`: glued beside that block's drag handle (prefer left)
 * - `point`: cursor-anchored fallback (e.g. callout background right-click)
 *
 * Portaled to document.body so `position: fixed` is viewport-true (doc sheets
 * use CSS transforms that would otherwise offset fixed descendants).
 */
export const BlockHandleMenu = ({
  anchorBlockId,
  point,
  onClose,
  children,
}) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return undefined;

    const place = () => {
      const live = liveHandleRect(anchorBlockId);
      if (live) {
        setPos(positionMenuBesideAnchor(el, live));
      } else if (point) {
        setPos(clampFloatingMenuPosition(el, point));
      }
    };

    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchorBlockId, point]);

  useEffect(() => {
    const isAnchorHandle = (target) => {
      if (!anchorBlockId || !target?.closest) return false;
      const handle = target.closest('.block-drag-handle');
      if (!handle) return false;
      return (
        handle.closest('[data-block-id]')?.getAttribute('data-block-id') ===
        anchorBlockId
      );
    };

    const onPointerDownCapture = (e) => {
      const menu = menuRef.current;
      if (!menu) return;
      // Capture phase so editor stopPropagation can't keep the menu open.
      if (menu.contains(e.target)) return;
      // Leave the anchor handle alone — mouseup toggles the menu closed.
      if (isAnchorHandle(e.target)) return;
      onClose?.();
    };

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    };

    // Defer so the opening gesture can't immediately dismiss us.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDownCapture, true);
    }, 0);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose, anchorBlockId]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={menuRef}
      className="callout-menu block-handle-menu"
      style={{
        left: pos ? pos.x : 0,
        top: pos ? pos.y : 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
};
